import { Injectable } from '@angular/core';
import {
  AccountPaymentMethod,
  Attachment,
  EmailBody,
  GetAttachmentUrlResponse,
  RemittanceEmail,
  RemittanceFile,
  SendEmailRequest,
} from '../models';
import { AppConfigSettings } from '@arc/arc-shared-frontend';

@Injectable({
  providedIn: 'root',
})
export class RemittanceEmailHelperService {
  constructor(private appConfigSettings: AppConfigSettings) {}

  private buildAttachment(
    remitanceFile: RemittanceFile,
    attachment: GetAttachmentUrlResponse,
    emailRequest: RemittanceEmail,
  ): Attachment {
    return {
      fileBucket: attachment.fileBucket,
      fileKey: attachment.fileKey,
      fileName: this.buildAttachmentName(emailRequest, remitanceFile.name),
      mimeType: remitanceFile.type,
    };
  }

  buildAttachmentName(
    emailRequest: RemittanceEmail,
    remittanceFileName: string,
  ): string {
    const {
      amount,
      buyerCompanyName,
      buyerAccountNumber,
      transactionReferenceNumber,
    } = emailRequest;
    return `${amount}.${buyerCompanyName}.${buyerAccountNumber}.${transactionReferenceNumber}.${remittanceFileName}`;
  }

  private buildBody(emailRequest: RemittanceEmail): EmailBody {
    const {
      buyerAccountNumber,
      buyerCompanyName,
      paymentDate,
      amount,
      paymentNotes,
      referenceNumber,
      referenceDate,
      paymentMethod,
      userEmail,
      isBuyerOriginApp,
      originatingApplication,
      receiptEmails,
    } = emailRequest;

    const lines = [
      `Account #: ${buyerAccountNumber}`,
      `Account Name: ${buyerCompanyName}`,
      ...(isBuyerOriginApp
        ? [`Email Address: ${userEmail}`]
        : [`Email Address: ${receiptEmails}`]),
      `Payment Date: ${paymentDate}`,
      `Total Payment Amount: $${amount.toFixed(2)}`,
      `Payment Method: ${
        paymentMethod === AccountPaymentMethod.Ach ? 'BANK' : 'Card'
      }`,
      `Reason: ${paymentNotes}`,
      ...(referenceNumber ? [`Reference #: ${referenceNumber}`] : []),
      ...(referenceDate ? [`Reference date: ${referenceDate}`] : []),
      `Originating Application: ${this.originApplicationMapperForEmail(
        originatingApplication,
      )}`,
    ];

    return {
      body: lines.join('\n'),
      mimeType: 'text/plain',
    };
  }

  private originApplicationMapperForEmail(
    originatingApplication: string,
  ): string {
    switch (originatingApplication.toLowerCase()) {
      case 'paymentlinks':
        return 'paymentlinks';
      case 'collections':
        return 'collections';
      case 'clientconnect':
        return 'clientconnect';
      case 'eic':
        return 'eInvoice Connect';
      case 'invoicing':
        return 'Invoicing';
      default:
        return '';
    }
  }

  getSendEmailRequest(
    remittanceFile: RemittanceFile,
    attachment: GetAttachmentUrlResponse,
    emailRequest: RemittanceEmail,
  ): SendEmailRequest {
    return {
      applicationName: this.appConfigSettings.getconfig('application'),
      from: { email: 'noreply@billtrust.com' },
      to: emailRequest.sendToEmails.map((email) => ({
        email,
      })),
      subject: `Payment remittance for $${emailRequest.amount.toFixed(2)}`,
      body: [this.buildBody(emailRequest)],
      attachments: [
        this.buildAttachment(remittanceFile, attachment, emailRequest),
      ],
    };
  }
}
